import { FC, useState } from 'react';
import { Modal } from '@mantine/core';
import { Encounter, Patient } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { EncounterForm } from '@/components/forms/EncounterForm';
import ConfirmCloseDialog from '@/components/ConfirmCloseDialog';

interface EncounterModalProps {
  onEncounterCreated?: (encounter: Encounter) => void;
  opened: boolean;
  onClose: () => void;
  patient: Patient;
  encounter?: Encounter;
}

/**
 * Modal wrapper for encounter creation/editing in patient encounters tab
 * Uses the unified EncounterForm component
 *
 * @param props - Component props
 * @param props.onEncounterCreated - Callback when encounter is created/updated
 * @param props.opened - Whether the modal is open
 * @param props.onClose - Callback to close the modal
 * @param props.patient - The patient for this encounter
 * @param props.encounter - Optional encounter to edit
 * @returns JSX.Element
 */
export const EncounterModal: FC<EncounterModalProps> = ({
  onEncounterCreated,
  opened,
  onClose,
  patient,
  encounter,
}) => {
  const { t } = useTranslation(['encounters']);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const handleEncounterCreated = (encounter: Encounter) => {
    onEncounterCreated?.(encounter);
    onClose();
  };

  const handleGuardedClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleGuardedClose}
        size="lg"
        title={
          encounter
            ? t('edit', { ns: 'encounters' })
            : t('new', { ns: 'encounters' })
        }
        closeOnClickOutside={!hasUnsavedChanges}
        closeOnEscape={!hasUnsavedChanges}
      >
        <EncounterForm
          patient={patient}
          onEncounterCreated={handleEncounterCreated}
          showPatientInfo={true}
          showAdditionalNotes={true}
          onFormStateChange={setHasUnsavedChanges}
          showDiscardButton={true}
          encounter={encounter}
        />
      </Modal>

      <ConfirmCloseDialog
        opened={showConfirmClose}
        onCancel={() => setShowConfirmClose(false)}
        onConfirm={() => {
          setShowConfirmClose(false);
          onClose();
        }}
      />
    </>
  );
};
